import React, { useEffect } from 'react';
import MarketingLayout from '../components/MarketingLayout';

const LAST_UPDATED = 'April 27, 2026';

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Service — PitchFriendly';
  }, []);

  return (
    <MarketingLayout>
      <section className="bg-white border-b border-gray-100 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-gray-400">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 prose prose-gray max-w-none">
          <div className="space-y-8 text-sm text-gray-600 leading-relaxed">

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using PitchFriendly ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. These Terms apply to all visitors, users, and others who access or use the Service.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Description of Service</h2>
              <p>
                PitchFriendly is a software-as-a-service (SaaS) platform that provides PR professionals with tools for managing reporter relationships, conducting outreach campaigns, tracking coverage, and related activities. We reserve the right to modify, suspend, or discontinue the Service at any time with reasonable notice.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">3. Account Registration</h2>
              <p>
                To access the Service, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete. You are responsible for safeguarding your account credentials and for all activities that occur under your account.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Acceptable Use</h2>
              <p>You agree not to use the Service to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Send unsolicited commercial email (spam) in violation of applicable law</li>
                <li>Violate any applicable law, regulation, or third-party rights</li>
                <li>Upload or transmit viruses or malicious code</li>
                <li>Attempt to gain unauthorized access to the Service or its infrastructure</li>
                <li>Use the Service to harass, abuse, or harm any person</li>
                <li>Scrape or extract data from the Service in an unauthorized manner</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Email Sending Policy</h2>
              <p>
                PitchFriendly provides email sending capabilities. By using these features, you agree to comply with all applicable anti-spam laws, including the CAN-SPAM Act (US), CASL (Canada), and GDPR (EU). You are solely responsible for ensuring that recipients have a legitimate basis to receive your emails. PitchFriendly reserves the right to suspend email sending for accounts that violate these policies.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Payment and Billing</h2>
              <p>
                Paid plans are billed in advance on a monthly basis. All fees are non-refundable except as required by law. You authorize us to charge your payment method for all applicable fees. If your payment fails, we may suspend your access to the Service. You can cancel your subscription at any time; cancellation takes effect at the end of the current billing period.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Intellectual Property</h2>
              <p>
                The Service and its original content, features, and functionality are and will remain the exclusive property of PitchFriendly and its licensors. Your content (contacts, data, messages you create) remains yours. You grant PitchFriendly a limited license to use your content solely to provide the Service.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Data and Privacy</h2>
              <p>
                Your use of the Service is subject to our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand our practices.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Disclaimer of Warranties</h2>
              <p>
                THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">10. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PITCHFRIENDLY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, ARISING OUT OF YOUR USE OF THE SERVICE. OUR AGGREGATE LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU IN THE TWELVE MONTHS PRECEDING THE CLAIM.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">11. Termination</h2>
              <p>
                We may terminate or suspend your account at any time for violations of these Terms, with or without notice. Upon termination, your right to use the Service will immediately cease. We will retain your data for 30 days following termination, after which it may be deleted.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will provide notice of material changes by email or by posting a notice in the Service. Continued use of the Service after changes constitutes acceptance of the updated Terms.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">13. Contact</h2>
              <p>
                If you have questions about these Terms, please contact us at <a href="mailto:legal@pitchfriendly.com" className="text-blue-600 hover:underline">legal@pitchfriendly.com</a>.
              </p>
            </div>

          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
